import React from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner
} from 'reactstrap'
import PropTypes from 'prop-types'
import { v1 as uuidv1 } from 'uuid'

import { withTranslation } from 'react-i18next'

import AlertMessage from '../../components/AlertMessage'
import FormInputField from '../../components/FormInputField'
import PageLoader from '../../components/PageLoader'
import rolesActions from '../../roles/actions'
import usersActions from './actions'

class UserModal extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      email: '',
      id: null,
      role: '',
      showRoleRequired: false
    }
  }

  componentDidMount() {
    const { dispatch, fetchingStatus } = this.props

    if (fetchingStatus !== 'succeeded') dispatch(rolesActions.fetchAll())
  }

  componentDidUpdate(prevProps) {
    const {
      id,
      invitationStatus,
      modalIsOpen,
      updateStatus
    } = this.props

    const {
      invitationStatus: prevInvitationStatus,
      modalIsOpen: prevModalIsOpen,
      updateStatus: prevUpdateStatus
    } = prevProps

    const { id: idFromState } = this.state

    if (prevModalIsOpen === false && modalIsOpen === true) {
      if (idFromState === null && id) this.updateUserId()
    }

    if (prevInvitationStatus === 'inviting') {
      if (invitationStatus === 'failed') {
        this.showFieldErrors()
      } else if (invitationStatus === 'succeeded') {
        this.onToggle()
      }
    }

    if (prevUpdateStatus === 'updating') {
      if (updateStatus === 'failed') {
        this.showFieldErrors()
      } else if (updateStatus === 'succeeded') {
        this.onToggle()
      }
    }
  }

  handleSubmit(event) {
    const { dispatch } = this.props

    const { email, id, role } = this.state

    event.preventDefault()

    if (email === '') {
      this.emailField.showAsInvalid()
    }

    if (role === '') {
      this.setState({
        showRoleRequired: true
      })
    }

    if (email !== '' && role !== '') {
      if (id === null) {
        dispatch(usersActions.create(email, role))
      } else {
        dispatch(usersActions.update(id, role))
      }
    }
  }

  onToggle() {
    const { onToggle } = this.props

    onToggle()

    this.setState({
      email: '',
      id: null,
      role: ''
    })
  }

  setEmail({ target: { value } }) {
    this.setState({
      email: value
    })
  }

  setRole(role) {
    this.setState({
      role,
      showRoleRequired: false
    })
  }

  showFieldErrors() {
    const { invitationError, updateError } = this.props

    if (invitationError && invitationError.email) {
      this.emailField.showAsInvalid(invitationError.email[0])
    }

    if (invitationError && invitationError.role) {
      this.roleField.showAsInvalid(invitationError.role[0])
    }

    if (updateError && updateError.role) {
      this.roleField.showAsInvalid(updateError.role[0])
    }
  }

  getGenericError() {
    const { invitationError, updateError } = this.props

    // Return generic error if it's a string (not field-specific)
    if (invitationError && typeof invitationError === 'string') {
      return invitationError
    }
    if (updateError && typeof updateError === 'string') {
      return updateError
    }
    return null
  }

  updateUserId() {
    const { id, users } = this.props

    const user = users.find((item) => item.id === id)

    this.setState({
      email: user.email,
      id,
      invitation: user.invitation,
      role: user.role
    })
  }

  render() {
    const {
      currentUserRole,
      fetchingError,
      fetchingStatus,
      invitationStatus,
      modalIsOpen,
      roles,
      t,
      updateStatus
    } = this.props

    const {
      email,
      id,
      invitation,
      role,
      showRoleRequired
    } = this.state

    const isSubmitting = invitationStatus === 'inviting'
      || updateStatus === 'updating'
    const genericError = this.getGenericError()

    return (
      <Modal isOpen={modalIsOpen} toggle={() => this.onToggle()}>
        <Form>
          <ModalHeader toggle={() => this.onToggle()}>
            {t(`UserModal.titles.${id ? 'edit' : 'invite'}`)}
          </ModalHeader>
          <ModalBody>
            {(['idle', 'fetching'].includes(fetchingStatus)) && (
              <PageLoader message={t('UserModal.fetchingRoles')} />
            )}

            {fetchingStatus === 'failed' && (
              <AlertMessage>{fetchingError}</AlertMessage>
            )}

            {fetchingStatus === 'succeeded' && (
              <>
                <FormInputField
                  disabled={id !== null} // Linguiini doesn't support changing email address yet.
                  inputId="email"
                  inputName="email"
                  inputType="email"
                  labelText={
                    invitation
                      ? t('UserModal.fields.invitation.email')
                      : t('UserModal.fields.email')
                  }
                  onChange={(event) => this.setEmail(event)}
                  ref={(component) => { this.emailField = component }}
                  value={email}
                />
                <FormGroup className="position-relative">
                  <Label for="roles">{t('UserModal.roles')}</Label>
                  <Input
                    id="roles"
                    invalid={showRoleRequired}
                    disabled={currentUserRole !== 'admin'}
                    name="roles"
                    onChange={({ target: { value } }) => this.setRole(value)}
                    type="select"
                    ref={(component) => { this.roleField = component }}
                    value={role}
                  >
                    {id === null && (
                      <option>{t('UserModal.pleaseSelectRole')}</option>
                    )}

                    {roles.map((item) => (
                      <option
                        key={uuidv1()}
                        value={item}
                      >
                        {item}
                      </option>
                    ))}
                  </Input>
                  {currentUserRole !== 'admin' && (
                    <FormText>{t('warnings.requiresAdminRole', { ns: 'commons' })}</FormText>
                  )}
                  {showRoleRequired && (
                    <FormFeedback tooltip>
                      {t('errors.required_field', { ns: 'commons' })}
                    </FormFeedback>
                  )}
                </FormGroup>
              </>
            )}

            {genericError && (
              <AlertMessage>{genericError}</AlertMessage>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              disabled={isSubmitting}
              onClick={() => this.onToggle()}
            >
              {t('buttons.cancel', { ns: 'commons' })}
            </Button>
            <Button
              color="primary"
              disabled={isSubmitting}
              onClick={(event) => this.handleSubmit(event)}
              type="submit"
            >
              {isSubmitting && <Spinner size="sm" className="me-2" />}
              {id ? t('buttons.save', { ns: 'commons' }) : t('UserModal.buttons.invite')}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    )
  }
}

UserModal.propTypes = {
  currentUserRole: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  fetchingError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  fetchingStatus: PropTypes.string,
  id: PropTypes.string,
  invitationError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  invitationStatus: PropTypes.string,
  modalIsOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(PropTypes.string),
  t: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(PropTypes.shape({
    email: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    invitation: PropTypes.bool.isRequired
  })),
  updateError: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  updateStatus: PropTypes.string
}

UserModal.defaultProps = {
  currentUserRole: null,
  fetchingError: null,
  fetchingStatus: 'idle',
  id: null,
  invitationError: null,
  invitationStatus: 'idle',
  roles: null,
  updateError: null,
  updateStatus: 'idle',
  users: null
}

const mapStateToProps = ({
  authentication: {
    role: currentUserRole
  },
  roles: {
    fetchingError,
    fetchingStatus,
    roles
  },
  users: {
    items: users,
    invitationError,
    invitationStatus,
    updateError,
    updateStatus
  }
}) => ({
  currentUserRole,
  fetchingError,
  fetchingStatus,
  invitationError,
  invitationStatus,
  roles,
  updateError,
  updateStatus,
  users
})

export default withTranslation(['UsersPage', 'commons'])(
  connect(mapStateToProps)(UserModal)
)
